import React, { useRef, useState } from 'react';
import { FaHashtag, FaPaperPlane, FaTimes } from 'react-icons/fa';
import Collapse from 'react-bootstrap/Collapse';
import TagChip from './TagChip';
import TagPicker from './TagPicker';

const MAX_TAGS = 5;
const MAX_LENGTH = 2000;

/**
 * ChatInput — message composer with tag attachment support.
 *
 * Props:
 *   clubGuid      string
 *   listenerGuid  string — used to scope the taggable endpoint to this thread
 *   onSend        ({ message: string, tags: Array }) => Promise<void>
 *   disabled      bool
 */
export default function ChatInput({ clubGuid, listenerGuid, onSend, disabled = false }) {
    const [text, setText] = useState('');
    const [tags, setTags] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const textareaRef = useRef(null);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSend = async () => {
        const trimmed = text.trim();
        if (!trimmed || isSending || disabled) return;

        setIsSending(true);
        try {
            await onSend({ message: trimmed, tags });
            setText('');
            setTags([]);
            textareaRef.current?.focus();
        } finally {
            setIsSending(false);
        }
    };

    const handleTagSelect = (tag) => {
        // Prevent duplicates by guid
        if (tags.some((t) => t.guid === tag.guid)) return;
        if (tags.length >= MAX_TAGS) return;
        setTags((prev) => [...prev, tag]);
    };

    const removeTag = (guid) => {
        setTags((prev) => prev.filter((t) => t.guid !== guid));
    };

    const canSend = text.trim().length > 0 && !isSending && !disabled;

    return (
        <div className="chat-input">
            {/* Tag chips row */}
            {tags.length > 0 && (
                <div className="chat-input__tags">
                    {tags.map((tag) => (
                        <TagChip
                            key={tag.guid}
                            type={tag.type}
                            displayText={tag.displayText}
                            guid={tag.guid}
                            removable
                            onRemove={() => removeTag(tag.guid)}
                        />
                    ))}
                </div>
            )}

            <div className="chat-input__row">
                {/* Tag picker toggle */}
                <button
                    className={`chat-input__tag-btn ${showPicker ? 'chat-input__tag-btn--active' : ''}`}
                    onClick={() => setShowPicker((v) => !v)}
                    disabled={disabled || tags.length >= MAX_TAGS}
                    title="Tag an episode, key takeaway, or practice"
                    type="button"
                >
                    <FaHashtag />
                </button>

                {/* Text area */}
                <textarea
                    ref={textareaRef}
                    className="chat-input__textarea"
                    placeholder="Type a message… Press Enter to send, Shift+Enter for new line"
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isSending}
                    rows={3}
                />

                {/* Send button */}
                <button
                    className="chat-input__send"
                    onClick={handleSend}
                    disabled={!canSend}
                    type="button"
                    title="Send message"
                >
                    {isSending ? (
                        <span className="chat-input__spinner" />
                    ) : (
                        <FaPaperPlane />
                    )}
                </button>
            </div>

            <Collapse in={showPicker}>
                <div>
                    <TagPicker
                        clubGuid={clubGuid}
                        listenerGuid={listenerGuid}
                        onSelect={(tag) => { handleTagSelect(tag); setShowPicker(false); }}
                        onClose={() => setShowPicker(false)}
                    />
                </div>
            </Collapse>

            {text.length > MAX_LENGTH * 0.9 && (
                <div className="chat-input__char-count">
                    {text.length}/{MAX_LENGTH}
                </div>
            )}
        </div>
    );
}
